import { type AdUnitConfiguration } from '@cx-web/feature-gam-advertising';

interface AdSlotProps {
  adUnitConfiguration: AdUnitConfiguration[];
  /**
   * Optional wrapper class applied around the slot's div, e.g. to reserve a scroll range for a sticky
   * placement. Only rendered alongside the div itself — when the placement doesn't render (see below),
   * no wrapper renders either, so nothing reserves layout space for a slot that was never eligible in
   * the first place.
   */
  containerClassName?: string;
  placement: NonNullable<AdUnitConfiguration['placement']>;
}

/**
 * Renders the target `<div>` for a single ad placement (e.g. `leaderboard`, `inline-banner`, `mpu`,
 * `mobile-sticky-bottom`), wherever in the page tree that placement physically belongs — unlike
 * `GoogleAdUnit`, which only bootstraps googletag once for the whole configuration and renders nothing.
 *
 * Renders nothing when the placement isn't present in `adUnitConfiguration` (ads disabled/repub
 * ineligible — the existing blank-space/collapse behaviour is unaffected since GPT only reserves space
 * once a slot's div exists) or when it's an `inject` slot: those target nodes are owned by the
 * layout/CSS that already renders them (e.g. the skyscraper rails in DynamicPageTemplate), so an
 * `<AdSlot>` for them would create a duplicate DOM id.
 */
const AdSlot = ({ adUnitConfiguration, containerClassName, placement }: AdSlotProps) => {
  const slot = adUnitConfiguration.find(config => config.placement === placement);

  if (!slot || slot.injectOrCreate !== 'create') {
    return null;
  }

  const div = <div data-testid={`${slot.targetNode}-testid`} id={slot.targetNode} />;

  return containerClassName ? <div className={containerClassName}>{div}</div> : div;
};

export default AdSlot;
